import tailorsAPI from '@/api/sorority-wars/tailorsAPI'

const normalizeErrors = errors => {
  if (Array.isArray(errors)) {
    return errors.reduce((all, error) => all.concat(normalizeErrors(error)), [])
  }
  if (errors && typeof errors === 'object') {
    return Object.keys(errors).reduce((all, key) => all.concat(normalizeErrors(errors[key])), [])
  }
  return errors ? [errors] : []
}

const responseErrors = error => {
  if (error.response && error.response.status === 404) return ['TAILOR_NOT_FOUND']

  if (error.response && error.response.data) {
    if (error.response.data.errors) return normalizeErrors(error.response.data.errors)
    if (error.response.data.error) return normalizeErrors(error.response.data.error)
  }

  return ['Unable to complete the Tailor request.']
}

const state = {
  tailors: [],
  activeTailorId: null,
  selectedTailor: null,
  parts: [],
  collectionLoading: false,
  detailLoading: false,
  partsLoading: false,
  stitching: false,
  collectionErrors: [],
  detailErrors: [],
  partsErrors: [],
  stitchErrors: [],
  lastStitch: null
}

const mutations = {
  setTailors (state, tailors) {
    state.tailors = Array.isArray(tailors) ? tailors : []
  },
  setActiveTailorId (state, tailorId) {
    state.activeTailorId = tailorId
  },
  setSelectedTailor (state, tailor) {
    state.selectedTailor = tailor || null
  },
  setParts (state, parts) {
    state.parts = Array.isArray(parts) ? parts : []
  },
  setCollectionLoading (state, loading) {
    state.collectionLoading = loading
  },
  setDetailLoading (state, loading) {
    state.detailLoading = loading
  },
  setPartsLoading (state, loading) {
    state.partsLoading = loading
  },
  setStitching (state, stitching) {
    state.stitching = stitching
  },
  setCollectionErrors (state, errors) {
    state.collectionErrors = errors || []
  },
  setDetailErrors (state, errors) {
    state.detailErrors = errors || []
  },
  setPartsErrors (state, errors) {
    state.partsErrors = errors || []
  },
  setStitchErrors (state, errors) {
    state.stitchErrors = errors || []
  },
  setLastStitch (state, result) {
    state.lastStitch = result || null
  },
  replaceTailor (state, tailor) {
    if (!tailor || typeof tailor.id === 'undefined') return

    state.tailors = state.tailors.map(item => {
      return String(item.id) === String(tailor.id) ? { ...item, ...tailor } : item
    })
  },
  resetDetail (state) {
    state.selectedTailor = null
    state.parts = []
    state.detailErrors = []
    state.partsErrors = []
    state.stitchErrors = []
    state.lastStitch = null
  }
}

const actions = {
  getTailors ({ commit }) {
    commit('setCollectionLoading', true)
    commit('setCollectionErrors', [])

    return tailorsAPI.getAll()
      .then(res => {
        if (res.data.success) {
          commit('setTailors', res.data.tailors)
        } else {
          const errors = normalizeErrors(res.data.errors || ['Unable to load Tailor designs.'])
          commit('setTailors', [])
          commit('setCollectionErrors', errors)
        }
        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)
        commit('setTailors', [])
        commit('setCollectionErrors', errors)
        return { success: false, errors }
      })
      .finally(() => commit('setCollectionLoading', false))
  },
  loadDetail ({ commit, dispatch }, tailorId) {
    commit('setActiveTailorId', tailorId)
    commit('resetDetail')

    return Promise.all([
      dispatch('getTailor', tailorId),
      dispatch('getParts', tailorId)
    ])
  },
  getTailor ({ state, commit }, tailorId) {
    commit('setDetailLoading', true)
    commit('setDetailErrors', [])

    return tailorsAPI.getOne(tailorId)
      .then(res => {
        if (String(state.activeTailorId) !== String(tailorId)) return res.data

        if (res.data.success && res.data.tailor) {
          commit('setSelectedTailor', res.data.tailor)
          commit('replaceTailor', res.data.tailor)
        } else {
          commit('setSelectedTailor', null)
          commit('setDetailErrors', normalizeErrors(res.data.errors || ['Unable to load this Tailor design.']))
        }
        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)
        if (String(state.activeTailorId) === String(tailorId)) {
          commit('setSelectedTailor', null)
          commit('setDetailErrors', errors)
        }
        return { success: false, errors }
      })
      .finally(() => {
        if (String(state.activeTailorId) === String(tailorId)) commit('setDetailLoading', false)
      })
  },
  getParts ({ state, commit }, tailorId) {
    commit('setPartsLoading', true)
    commit('setPartsErrors', [])

    return tailorsAPI.getParts(tailorId)
      .then(res => {
        if (String(state.activeTailorId) !== String(tailorId)) return res.data

        if (res.data.success) {
          commit('setParts', res.data.parts)
        } else {
          commit('setParts', [])
          commit('setPartsErrors', normalizeErrors(res.data.errors || ['Unable to load recipe parts.']))
        }
        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)
        if (String(state.activeTailorId) === String(tailorId)) {
          commit('setParts', [])
          commit('setPartsErrors', errors)
        }
        return { success: false, errors }
      })
      .finally(() => {
        if (String(state.activeTailorId) === String(tailorId)) commit('setPartsLoading', false)
      })
  },
  stitch ({ state, commit, dispatch }, tailorId) {
    if (state.stitching) return Promise.resolve({ success: false, errors: ['TAILOR_IN_FLIGHT'] })

    commit('setStitching', true)
    commit('setStitchErrors', [])
    commit('setLastStitch', null)

    return tailorsAPI.claim(tailorId)
      .then(res => {
        const data = res.data || {}
        if (String(state.activeTailorId) !== String(tailorId)) return data

        if (data.tailor) {
          commit('setSelectedTailor', data.tailor)
          commit('replaceTailor', data.tailor)
        }

        if (data.success) {
          commit('setLastStitch', data)
          return Promise.all([
            dispatch('getTailors'),
            dispatch('getTailor', tailorId),
            dispatch('getParts', tailorId),
            dispatch('equipment/getInventory', null, { root: true })
          ]).then(() => data)
        }

        const errors = normalizeErrors(data.errors || ['Unable to stitch this Glam.'])
        commit('setStitchErrors', errors)
        commit('setLastStitch', data)

        if (errors.indexOf('TAILOR_INCOMPLETE') !== -1) {
          return Promise.all([
            dispatch('getTailors'),
            dispatch('getTailor', tailorId),
            dispatch('getParts', tailorId)
          ]).then(() => data)
        }

        return data
      })
      .catch(error => {
        const errors = responseErrors(error)
        if (String(state.activeTailorId) === String(tailorId)) commit('setStitchErrors', errors)
        return { success: false, errors }
      })
      .finally(() => commit('setStitching', false))
  },
  clearDetail ({ commit }) {
    commit('setActiveTailorId', null)
    commit('resetDetail')
    commit('setDetailLoading', false)
    commit('setPartsLoading', false)
  }
}

const getters = {
  tailorById: state => tailorId => {
    return state.tailors.find(tailor => String(tailor.id) === String(tailorId)) || null
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true
}
